import { supabase } from './supabase';
import { putToR2 } from './upload-r2';

// The contract form is rendered by the CRM SPA itself. It must NOT use the
// vitrina share base (propiedades.*), which is an OG-preview host routed through
// the vitrina-preview edge function and has no /formulario handler — it just
// 302s to the apex. Use the app's own domain.
const CONTRACT_BASE =
  import.meta.env.VITE_WEB_URL || 'https://intercambioinmobiliario.com';

export function buildContractUrl(token: string): string {
  return `${CONTRACT_BASE}/formulario/${token}`;
}

export interface ContractProperty {
  id: string;
  ii_id?: string | null;
  title?: string | null;
  operation?: string | null;
  price?: number | null;
  price_label?: string | null;
  property_type_name?: string | null;
  department_name?: string | null;
  city_name?: string | null;
  cover_photo_url?: string | null;
}

export interface ContractData {
  status: 'borrador' | 'firmado' | 'expirado';
  owner_name?: string | null;
  agent_name?: string | null;
  agency_name?: string | null;
  agency_logo?: string | null;
  intro_message?: string | null;
  commission_pct: number;
  property?: ContractProperty | null;
  agreed_commission?: boolean;
  agreed_public_promotion?: boolean;
  showing_days?: string[];
  showing_times?: string[];
  showing_other?: string[];
  owner_notes?: string | null;
  owner_email?: string | null;
  signature_url?: string | null;
  signed_at?: string | null;
}

/** Agent: create a contract for one of their properties. */
export async function createContract(
  ownerId: string,
  propertyId: string,
  introMessage: string,
  commissionPct: number,
): Promise<{ id: string; owner_name: string; owner_phone: string; property_title: string }> {
  if (!supabase) throw new Error('Supabase no inicializado');
  const { data, error } = await supabase.rpc('create_owner_authorization', {
    p_owner_id: ownerId,
    p_property_id: propertyId,
    p_intro_message: introMessage,
    p_commission_pct: commissionPct,
  });
  if (error) throw new Error(error.message);
  return data as { id: string; owner_name: string; owner_phone: string; property_title: string };
}

/** Anon (owner form): load the contract by token. */
export async function fetchContract(token: string): Promise<ContractData | { error: string }> {
  if (!supabase) throw new Error('Supabase no inicializado');
  const { data, error } = await supabase.rpc('get_owner_authorization', { p_token: token });
  if (error) return { error: error.message };
  return data as ContractData | { error: string };
}

/** Anon (owner form): upload the signature PNG to R2 via the token-gated presign. */
export async function uploadSignature(token: string, blob: Blob): Promise<string> {
  if (!supabase) throw new Error('Supabase no inicializado');
  const { data, error } = await supabase.functions.invoke<{ uploadUrl: string; publicUrl: string }>(
    'signature-presign',
    { body: { token, size: blob.size } },
  );
  if (error || !data) throw new Error(error?.message || 'No se pudo preparar la firma');
  try {
    await putToR2(data.uploadUrl, blob, 'image/png');
  } catch (err) {
    throw new Error(`Error al subir la firma: ${(err as Error).message}`);
  }
  return data.publicUrl;
}

/** Anon (owner form): submit the signed authorization. */
export async function submitContract(
  token: string,
  payload: {
    agreedCommission: boolean;
    agreedPublicPromotion: boolean;
    showingDays: string[];
    showingTimes: string[];
    showingOther: string[];
    ownerNotes: string;
    ownerEmail: string;
    signatureUrl: string;
  },
): Promise<{ status?: string; error?: string }> {
  if (!supabase) throw new Error('Supabase no inicializado');
  const { data, error } = await supabase.rpc('submit_owner_authorization', {
    p_token: token,
    p_agreed_commission: payload.agreedCommission,
    p_agreed_public_promotion: payload.agreedPublicPromotion,
    p_showing_days: payload.showingDays,
    p_showing_times: payload.showingTimes,
    p_showing_other: payload.showingOther,
    p_owner_notes: payload.ownerNotes,
    p_owner_email: payload.ownerEmail,
    p_signature_url: payload.signatureUrl,
  });
  if (error) return { error: error.message };
  return data as { status?: string; error?: string };
}
